// Adaptors
import { MetaOSHostAdaptor } from "../MetaOSHostAdaptor";
// Constants
import { Environment, OutlookAppId } from "@ms/uno-constants/lib/local/AppConstants";
import { OutlookRingId } from "@ms/uno-constants/lib/local/OutlookConstants";
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Meta hub sdk
import * as metaOSAppSDK from "@microsoft/teams-js-uno";
// Models
import { generateRouteFromDeepLink } from "@ms/uno-routing";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { generateDeepLinkFromRoute } from "@ms/uno-routing";
import { generateRouteFromLegacyDeepLink } from "../../utils/LegacyTeamsDeepLinkUtils";
import { stringToEnum } from "@ms/uno-utilities/lib/local/EnumUtilities";
import { generateLogStringFromError } from "@ms/uno-telemetry/lib/local/utilities/LogUtilities";
/**
 * OutlookAdaptor provides Outlook-specific implementations for MetaOSHostAdaptor.
 *
 * This adaptor handles Outlook context validation, theme mapping, and environment-specific
 * root URL computation.
 *
 * @remarks
 * - Maps Outlook themes to Uno themes for consistent UI experience.
 * - Computes the root URL for different Outlook environments.
 * - Provides simplified context management.
 *
 * @extends MetaOSHostAdaptor
 */ export class OutlookAdaptor extends MetaOSHostAdaptor {
    /**
     * Validates ringId for Outlook context.
     * For Outlook, we use a simplified validation approach.
     * @returns validation error message. null if valid.
     */ validateRingId() {
        const { ringId: sessionRingId } = this.getConfig().sessionMetaData;
        const normalizedSessionRingId = this.normalizeRingId(sessionRingId);
        if (this.ringId && normalizedSessionRingId && this.ringId !== normalizedSessionRingId) {
            return `Outlook client ringId does not match session ringId [ClientRingId=${this.ringId}][SessionRingId=${sessionRingId}]`;
        }
        return null;
    }
    generateDeepLink(route) {
        // page Id is entity id of static tab, e.g- teamstasksppe.personalApp.myTasks
        const pageId = this.pageId;
        const appId = pageId?.startsWith("teamstasksppe") ? OutlookAppId.PPE : OutlookAppId.Prod;
        if (!pageId || !this.tenantId || !this.rootUrl) {
            this.loggers?.traceLogger.logTrace(0x1e15d356 /* tag_4f3nw */ , TraceLevel.Error, `generateDeepLink - Missing required deep link info`);
            return null;
        }
        const deepLink = generateDeepLinkFromRoute(route, this.loggers);
        if (!deepLink) {
            this.loggers?.traceLogger.logTrace(0x1e15d355 /* tag_4f3nv */ , TraceLevel.Error, `generateDeepLink - Unable to generate DeepLink from Route`);
            return null;
        }
        const subEntityId = deepLink;
        // before adding sub entity id to url, encode it
        // example: /v1/plan/planid -> %2Fv1%2Fplan%2Fplanid
        const encodedSubEntityId = encodeURIComponent(subEntityId);
        const outlookAppLinkUrl = `${this.rootUrl}/host`;
        const webUrl = `https://tasks.teams.microsoft.com/teamsui/personalApp/alltasklists`;
        const fullDeepLinkUrl = `${outlookAppLinkUrl}/${appId}/${pageId}/${encodedSubEntityId}` + `?tenantId=${this.tenantId}` + `&webUrl=${encodeURIComponent(webUrl)}`;
        return fullDeepLinkUrl;
    }
    async getInitialRoute() {
        const metaHubsContext = await this.getMetaOSAppContext();
        const encodedSubEntity = metaHubsContext.page.subPageId;
        try {
            if (encodedSubEntity) {
                const decodedPath = decodeURIComponent(encodedSubEntity);
                const routeFromNewDeepLink = generateRouteFromDeepLink(decodedPath, this.loggers, generateRouteFromLegacyDeepLink);
                if (routeFromNewDeepLink) {
                    return routeFromNewDeepLink;
                }
            }
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e15781c /* tag_4fx62 */ , TraceLevel.Error, `getInitialRoute - Error decoding subEntityId: ${e.message}`);
        }
        return null;
    }
    /**
     * Normalizes the ringId value for Outlook to accepted values/ null if not valid.
     * @param ringId
     */ normalizeRingId(ringId) {
        if (typeof ringId !== "string" || ringId.trim().length === 0) {
            return null;
        }
        const normalRingId = stringToEnum(ringId.trim().toLowerCase(), OutlookRingId);
        return normalRingId;
    }
    /**
     * Maps Outlook theme to Uno theme
     * @param theme Outlook theme
     * @returns Uno theme
     */ mapToUnoTheme(theme) {
        // TODO #2689038: Confirm on Outlook theme mapping
        switch(theme.trim().toLowerCase()){
            case "dark":
            case "black":
            case "darkgray":
                return PlannerTheme.Dark;
            case "default":
            case "light":
            case "white":
            case "colorful":
            default:
                return PlannerTheme.Light;
        }
    }
    /**
     * Returns the root URL for Outlook based on environment
     */ computeRootUrl() {
        // TODO #2689038: validate outlook urls for different environments.
        const environment = this.getConfig().sessionMetaData.environment;
        switch(environment){
            case Environment.Ppe:
            case Environment.Prod:
                return "https://outlook.cloud.microsoft";
            case Environment.Gcc:
            case Environment.Gcch:
            case Environment.Dod:
            case Environment.Agc:
            default:
                this.loggers?.traceLogger.logTrace(0x1e1880dd /* tag_4gid3 */ , TraceLevel.Error, `computeOutlookRootUrl Unknown environment. [Environment = ${environment}]`);
                return null;
        }
    }
    /**
     * Opens a file preview in the host application, specifically handling Outlook mail items.
     * Falls back to the base implementation if that fails.
     * @param fileUrl - The URL of the file to preview. In case of outlook attachments this is attachment url.
     * @param fileAlias - An alias or display name for the file.
     * @param route - Optional route descriptor for navigation context.
     * @param sharePointBaseUrl - Optional base URL for SharePoint integration.
     * @param sharePointListItemUniqueId - Optional unique ID for the SharePoint list item.
     */ openFilePreviewInHost(fileUrl, fileAlias, route, sharePointBaseUrl, sharePointListItemUniqueId) {
        const openedSuccessfully = this.openOutlookMailItem(fileUrl);
        if (openedSuccessfully) {
            this.loggers?.traceLogger.logTrace(0x1e14888f /* tag_4fi8p */ , TraceLevel.Info, "openFilePreviewInHost - Successfully opened mail item in Outlook");
            return;
        }
        // Fallback to base implementation if not opened successfully
        super.openFilePreviewInHost(fileUrl, fileAlias, route, sharePointBaseUrl, sharePointListItemUniqueId);
    }
    /**
     * Opens an Outlook mail item in Outlook itself using the provided URL.
     * @param url - The URL of the Outlook mail item to open.
     * @return True if the mail item was successfully opened, false otherwise.
     */ openOutlookMailItem(url) {
        if (!metaOSAppSDK.mail.isSupported()) {
            this.loggers?.traceLogger.logTrace(0x1e14c145 /* tag_4fmff */ , TraceLevel.Error, `openOutlookMailItem - Mail module is not supported`);
            return false;
        }
        try {
            if (!url || typeof url !== "string" || url.trim().length === 0) {
                throw new Error("Invalid or empty URL provided.");
            }
            const urlObject = new URL(url);
            const itemId = urlObject.searchParams.get("ItemID");
            if (!itemId) {
                throw new Error("ItemID not found in URL");
            }
            // openMailItem documentation link - https://learn.microsoft.com/en-us/javascript/api/@microsoft/teams-js/mail?view=msteams-client-js-latest#@microsoft-teams-js-mail-openmailitem
            metaOSAppSDK.mail.openMailItem({
                itemId
            });
            return true;
        } catch (e) {
            this.loggers?.traceLogger.logTrace(0x1e14c142 /* tag_4fmfc */ , TraceLevel.Error, `openOutlookMailItem failed - ${generateLogStringFromError(e)}`);
            return false;
        }
    }
    /**
     * Initializes state from current MetaOSAppContext for Outlook
     */ async initFromMetaOSAppContext() {
        const metaOSContext = await this.getContextFromMetaOSAppSDK();
        this.ringId = this.normalizeRingId(metaOSContext.app.host.ringId ?? null);
        this.pageId = metaOSContext.page.id;
        this.tenantId = metaOSContext.user?.tenant?.id ?? null;
    }
}
